import { useState, useContext, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/router';
import ChatSidebar from '@/components/ChatSidebar';
import UserContext from '@/components/UserContext';
import { AccountDialog } from '@/components/AccountDialog';
import { Session } from '@/services/supabase-types';
import { cn } from '@/utils';

interface LayoutProps {
  children: ReactNode | ((props: { sidebarOpen: boolean; setSidebarOpen: (open: boolean) => void }) => ReactNode);
  className?: string;
  containerClassName?: string;
  selectedSessionId?: string | null;
}

export default function Layout({ children, className, containerClassName, selectedSessionId }: LayoutProps) {
  const router = useRouter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { user, userDetails } = useContext(UserContext);

  // Hydration-safe initialization
  useEffect(() => {
    setIsClient(true);

    // Initialize sidebar state from localStorage or default
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) {
      setSidebarOpen(JSON.parse(saved));
    } else {
      setSidebarOpen(false);
    }

    // Enable animations after hydration
    setTimeout(() => setHasLoaded(true), 100);
  }, []);

  // Save sidebar state to localStorage (only after client hydration)
  useEffect(() => {
    if (isClient) {
      localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen, isClient]);

  const handleSessionSelect = async (session: Session) => {
    router.push(`/chats/${session.slug}`);
  };

  const handleNewChat = () => {
    // Force a refresh of the chat list
    setRefreshTrigger(prev => prev + 1);
    // Navigate to home
    router.push('/app');
  };

  return (
    <div className={cn("flex h-dvh w-full bg-white dark:bg-gray-900", className)}>
      <ChatSidebar
        user={user}
        userDetails={userDetails}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        hasLoaded={hasLoaded}
        selectedSessionId={selectedSessionId || null}
        onSessionSelect={handleSessionSelect}
        onNewChat={handleNewChat}
        refreshTrigger={refreshTrigger}
        onUserClick={() => setShowAccountDialog(true)}
      />

      {/* Main Content Area */}
      <div
        className={cn(
          'flex-1 flex flex-col min-h-dvh',
          hasLoaded ? 'transition-all duration-200' : 'transition-none',
          sidebarOpen ? 'md:ml-[260px]' : 'md:ml-12',
          containerClassName
        )}
      >
        {typeof children === 'function' ? children({ sidebarOpen, setSidebarOpen }) : children}
      </div>

      {/* Account Dialog */}
      <AccountDialog
        open={showAccountDialog}
        onClose={() => setShowAccountDialog(false)}
      />
    </div>
  );
}